import {
  autocompleteClasses,
  dividerClasses,
  filledInputClasses,
  inputBaseClasses,
  listItemIconClasses,
  menuItemClasses,
  selectClasses,
  type Components,
} from '@mui/material';
import { themeVars } from '../themeVars';
import { listItemPadding } from './list';
import {
  inputAdornmentIconButtonStyles,
  inputClearIconSizes,
  selectIconSizes,
  typographyStyles,
  type Theme,
} from './utils';

/**
 * Styles Autocomplete, Select, and Menu components should mirror each other
 */

export const menuPaperStyles = ({ theme }: { theme: Theme }) => ({
  backgroundColor: themeVars(theme).palette.background.level2,
  boxShadow: 'none',
  border: `1px solid ${themeVars(theme).palette.border.subtle}`,
  // Easier than Popper.js offset modifier
  marginTop: 1,
});

export const menuItemStyles = ({ theme }: { theme: Theme }) => ({
  ...typographyStyles(theme.typography.label2),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: listItemPadding,
  paddingRight: listItemPadding,
  minHeight: 38,
  backgroundClip: 'padding-box',
});

export const MuiMenu: Components<Theme>['MuiMenu'] = {
  styleOverrides: {
    root: {
      backgroundColor: 'transparent',
    },
    paper: menuPaperStyles,
    list: {
      paddingBlock: 0,
    },
  },
};

export const MuiMenuItem: Components<Theme>['MuiMenuItem'] = {
  styleOverrides: {
    root: ({ theme }) => {
      const palette = themeVars(theme).palette;
      return {
        ...menuItemStyles({ theme }),

        '&:hover': {
          textDecoration: 'none',
          backgroundColor: themeVars(theme).palette.action.hover,
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: 'transparent',
          },
        },
        '&:active': {
          backgroundColor: themeVars(theme).palette.action.pressed,
        },
        [`&.${menuItemClasses.selected}`]: {
          backgroundColor: palette.action.selected,
          borderColor: `rgba(${themeVars(theme).palette.action.selectedChannel} / 0.7)`,

          [`&.${menuItemClasses.focusVisible}`]: {
            backgroundColor: palette.action.selected,
          },
        },
        [`&.${menuItemClasses.selected}:hover`]: {
          backgroundColor: `rgba(${palette.action.selectedChannel} / ${themeVars(theme).palette.action.selectedOpacity + themeVars(theme).palette.action.hoverOpacity})`,
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: palette.action.selected,
          },
        },
        [`&.${menuItemClasses.selected}:active`]: {
          backgroundColor: `rgba(${palette.action.selectedChannel} / ${themeVars(theme).palette.action.selectedOpacity + themeVars(theme).palette.action.pressedOpacity})`,
        },
        [`&.${menuItemClasses.focusVisible}`]: {
          backgroundColor: themeVars(theme).palette.action.hover,
        },
        [`&.${menuItemClasses.disabled}`]: {
          opacity: themeVars(theme).palette.action.disabledOpacity,
        },
        [`&.${menuItemClasses.gutters} > .${listItemIconClasses.root}:first-of-type`]: {
          marginLeft: `calc(-${listItemPadding}px + ${theme.spacing(1.5)})`,
        },
        [`& + .${dividerClasses.root}`]: {
          marginTop: 0,
          marginBottom: 0,
        },
        [`& + .${dividerClasses.inset}`]: {
          marginLeft: 52,
        },
        [`& .${listItemIconClasses.root}`]: {
          minWidth: 20,
        },
      };
    },
  },
  variants: [
    {
      props: ({ ownerState }) => !!ownerState.divider,
      style: ({ theme }) => ({
        borderColor: themeVars(theme).palette.border.subtle,
        '&:last-of-type': {
          borderBottom: 0,
        },
      }),
    },
    {
      props: ({ ownerState }) => !!ownerState.dense,
      style: ({ theme }) => ({
        minHeight: 30,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        ...typographyStyles(theme.typography.label2),
        [`& .${listItemIconClasses.root} svg`]: {
          fontSize: '1.25rem',
        },
      }),
    },
  ],
};

export const MuiSelect: Components<Theme>['MuiSelect'] = {
  styleOverrides: {
    select: ({ theme }) => ({
      [`.${inputBaseClasses.sizeSmall} &`]: {
        paddingTop: 17,
        paddingBottom: 6,
        paddingRight: theme.spacing(3.5),
      },
    }),
    icon: ({ theme }) => ({
      color: themeVars(theme).palette.text.secondary,
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.short,
      }),
      '--size': `${selectIconSizes.medium}px`,
      width: 'var(--size)',
      height: 'var(--size)',
      top: 'calc(50% - var(--size) / 2)',
      right: 12,
      [`&.${selectClasses.iconOpen}`]: {
        transform: 'scaleY(-1)',
      },
      [`.${inputBaseClasses.sizeSmall} &`]: {
        '--size': `${selectIconSizes.small}px`,
      },
    }),
  },
};

export const MuiAutocomplete: Components<Theme>['MuiAutocomplete'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      [`& .${filledInputClasses.root}`]: {
        rowGap: 4,
        paddingTop: 21,
        paddingBottom: 3,
        [`& .${filledInputClasses.input}`]: {
          padding: '2px 4px',
        },
        [`& .${autocompleteClasses.endAdornment}`]: {
          right: 9,
        },
      },
      [`& .${filledInputClasses.root}.${inputBaseClasses.sizeSmall}`]: {
        minHeight: 40,
        paddingTop: 16,
        paddingBottom: 0,
        [`& .${filledInputClasses.input}`]: {
          padding: '1px 4px',
        },
      },
      [`& .${filledInputClasses.root}.${inputBaseClasses.hiddenLabel}`]: {
        paddingTop: 0,
        paddingBottom: 0,
        [`& .${autocompleteClasses.input}`]: {
          paddingTop: 16,
          paddingBottom: 17,
        },
      },
      [`& .${filledInputClasses.root}.${inputBaseClasses.hiddenLabel}.${inputBaseClasses.sizeSmall}`]: {
        [`& .${autocompleteClasses.input}`]: {
          paddingTop: 8,
          paddingBottom: 9,
        },
      },
      // Tag count label, e.g. "+3"
      [`& span.${autocompleteClasses.tag}`]: {
        ...typographyStyles(theme.typography.label2),
        // Align vertically with input placeholder
        paddingBlock: 2,
      },
      [`& .${inputBaseClasses.sizeSmall} span.${autocompleteClasses.tag}`]: {
        ...typographyStyles(theme.typography.label3),
      },
    }),
    paper: menuPaperStyles,
    listbox: ({ theme }) => {
      const palette = themeVars(theme).palette;
      return {
        padding: 0,

        [`& .${autocompleteClasses.option}`]: {
          ...menuItemStyles({ theme }),

          '&:last-of-type': {
            borderBottom: 0,
          },
          [`&.${autocompleteClasses.focused}`]: {
            backgroundColor: palette.action.hover,
            // Reset on touch devices, it doesn't add specificity
            '@media (hover: none)': {
              backgroundColor: 'transparent',
            },
            '&:active': {
              backgroundColor: palette.action.pressed,
            },
          },
          [`&.${autocompleteClasses.focusVisible}`]: {
            boxShadow: `inset 0 0 0 1px ${themeVars(theme).palette.border.focused}`,
            [`&.${autocompleteClasses.focused}`]: {
              backgroundColor: 'transparent',
            },
          },
          '&[aria-selected="true"]': {
            backgroundColor: palette.action.selected,
            [`&.${autocompleteClasses.focused}`]: {
              backgroundColor: `rgba(${palette.action.selectedChannel} / ${themeVars(theme).palette.action.selectedOpacity + themeVars(theme).palette.action.hoverOpacity})`,
              // Reset on touch devices, it doesn't add specificity
              '@media (hover: none)': {
                backgroundColor: palette.action.selected,
              },
            },
            [`&:active`]: {
              backgroundColor: `rgba(${palette.action.selectedChannel} / ${themeVars(theme).palette.action.selectedOpacity + themeVars(theme).palette.action.pressedOpacity})`,
            },
            [`&.${autocompleteClasses.focusVisible}`]: {
              boxShadow: `inset 0 0 0 1px ${themeVars(theme).palette.border.focused}`,
            },
          },

          [`& .${listItemIconClasses.root}:first-of-type`]: {
            marginLeft: `calc(-${listItemPadding}px + ${theme.spacing(1.5)})`,
          },
        },
      };
    },
    tag: {
      // Vertical margins make a single line of tags taller than an empty input.
      // Use a row gap on the input root so height only increases when tags wrap.
      margin: '0 2px',
    },
    popupIndicator: ({ theme }) => ({
      // Should be same as IconButton padding
      ...inputAdornmentIconButtonStyles('medium'),
      color: themeVars(theme).palette.text.secondary,
      padding: theme.spacing(0.5),
      transform: 'unset',
      '& svg': {
        height: selectIconSizes.medium,
        transition: theme.transitions.create('transform', {
          duration: theme.transitions.duration.short,
        }),
        width: selectIconSizes.medium,
      },
      [`.${inputBaseClasses.sizeSmall} & svg`]: {
        height: selectIconSizes.small,
        width: selectIconSizes.small,
      },
      [`.${inputBaseClasses.sizeSmall} &`]: {
        ...inputAdornmentIconButtonStyles('small'),
      },
      [`&.${autocompleteClasses.popupIndicatorOpen} svg`]: {
        transform: 'scaleY(-1)',
      },
    }),
    clearIndicator: ({ theme }) => ({
      ...inputAdornmentIconButtonStyles('medium'),
      color: themeVars(theme).palette.text.secondary,
      marginRight: 4,
      padding: theme.spacing(0.5),
      '& svg': {
        fontSize: inputClearIconSizes.medium,
        height: inputClearIconSizes.medium,
        width: inputClearIconSizes.medium,
      },
      [`.${inputBaseClasses.sizeSmall} & svg`]: {
        fontSize: inputClearIconSizes.small,
        height: inputClearIconSizes.small,
        width: inputClearIconSizes.small,
      },
      [`.${inputBaseClasses.sizeSmall} &`]: {
        ...inputAdornmentIconButtonStyles('small'),
      },
    }),
  },
};
